import {HttpClient, HttpHeaders, HttpParams, HttpResponse} from "@angular/common/http";
import {IPaginatedResponse, IService} from "maps-angular-components";
import {Injectable} from '@angular/core';
import {FormGroup} from "@angular/forms";
import {Observable} from "rxjs";
import {catchError} from "rxjs/operators";
import {handleError} from "../shared/util/error-util";
import {GrupoCarteira} from "./grupo-carteira";
import {ResultPage} from "./gerenciar-registro/result-page";
import {AssociacaoGrupoCarteiraVO} from "./associacao-grupo-carteira";

@Injectable({
  providedIn: 'root'
})
export class GrupoCarteiraService implements IService<GrupoCarteira> {

  url = '/v1/grupo-carteiras';
  private readonly httpHeaders = new HttpHeaders({'Content-Type': 'application/json'});

  constructor(private readonly httpclient: HttpClient) {
  }

  gravarGrupoCarteira(grupoCarteira: GrupoCarteira): Observable<any> {
    const headers = this.httpHeaders;
    const params: HttpParams = new HttpParams();
    const options = {headers, params};
    return this.httpclient.post(`${this.url}/create`, grupoCarteira, options).pipe();
  }

  updateGrupoCarteira(grupoCarteira: GrupoCarteira): Observable<any> {
    const headers = this.httpHeaders;
    const params: HttpParams = new HttpParams();
    const options = {headers, params};
    return this.httpclient.post(`${this.url}/update`, grupoCarteira, options).pipe();
  }

  montarGrupoCarteira(form: FormGroup): GrupoCarteira {
    const grupoCarteira = new GrupoCarteira();

    grupoCarteira.nome = form.get("nome").value.toUpperCase();
    if (form.get("descricao").value != null) {
      grupoCarteira.descricao = form.get("descricao").value.toUpperCase();
    } else {
      grupoCarteira.descricao = form.get("descricao").value;
    }
    return grupoCarteira;
  }

  search(model: any, pageSize: number): Observable<ResultPage> {
    const headers = this.httpHeaders;

    let params = this.createFormParam(model);

    params = params.set('size', pageSize.toString());
    params = params.set('page', model.page.toString());

    const options = {headers, params};
    return this.httpclient.get<ResultPage>(`${this.url}/all`, options).pipe(catchError(handleError));
  }

  private createFormParam(model: any) {
    let params: HttpParams = new HttpParams();
    Object.keys(model).forEach(item => {
      if (this.notNullNotEmpty(model[item])) params = params.set(item, model[item])
    });

    params = params.set('sort', 'nome,ASC');

    return params;
  }

  excluirGrupoCarteira(id: string): Observable<any> {
    const headers = this.httpHeaders;
    const options = {headers};

    return this.httpclient.delete(`${this.url}/remove/${id}`, options).pipe();
  }

  notNullNotEmpty(value: any): boolean {
    return value !== null && value !== "" && value !== undefined;
  }

  downloadModelo(): Observable<HttpResponse<Blob>> {
    return this.getExportDataFrom(`${this.url}/download-modelo`);
  }

  private getExportDataFrom(path: string): Observable<HttpResponse<Blob>> {
    const params = new HttpParams();
    return this.httpclient.get(path, {
      responseType: "blob",
      observe: "response",
      params
    }).pipe(catchError(handleError));
  }

  autocomplete(identificador: string, endpoint = ''): Observable<GrupoCarteira> {
    const headers = this.httpHeaders;
    const options = {headers};
    return this.httpclient.get<GrupoCarteira>(`${this.url}${endpoint}?nome=${encodeURIComponent(identificador)}&size=${10}`, options).pipe(catchError(handleError));
  }

  exportPdf(value: any): Observable<HttpResponse<Blob>> {
    const headers = this.httpHeaders;
    const params = this.createFormParam(value).set("size", '99999999');
    return this.httpclient.get(`${this.url}/export/pdf`, {
      headers,
      responseType: "blob",
      observe: "response",
      params
    }).pipe(catchError(handleError));
  }

  exportXlsx(value: any): Observable<HttpResponse<Blob>> {
    const headers = this.httpHeaders;
    const params = this.createFormParam(value).set("size", '99999999');
    return this.httpclient.get(`${this.url}/export/xlsx`, {
      headers,
      responseType: "blob",
      observe: "response",
      params
    }).pipe(catchError(handleError));
  }

  parsePaginatedResponse(response): IPaginatedResponse<GrupoCarteira> {
    return {
      total: null,
      data: response.map(item => new GrupoCarteira({id: item.id, nome: item.nome, descricao: item.descricao, carteiras: item.carteiras}))
    };
  }

  getAssociacoesGrupoCarteira(idGrupoCarteira: number, idCarteira: number, page: number, size: number): Observable<ResultPage> {
    let params = new HttpParams();

    if (idGrupoCarteira) params = params.set('idGrupoCarteira', idGrupoCarteira.toString());
    if (idCarteira) params = params.set('idCarteira', idCarteira.toString());
    params = params.set('page', page.toString());
    params = params.set('size', size.toString());
    params = params.set('sort', 'nome,ASC');

    return this.httpclient
      .get<ResultPage>(`${this.url}/associacoes`, { headers: this.httpHeaders, params })
      .pipe(catchError(handleError));
  }

  exportAssociacoesGrupoCarteiraPdf(idGrupoCarteira: number, idCarteira: number, page: number, size: number): Observable<HttpResponse<Blob>> {
    let params = new HttpParams();
    if (idGrupoCarteira) params = params.set('idGrupo', idGrupoCarteira.toString());
    if (idCarteira) params = params.set('idCarteira', idCarteira.toString());

    return this.httpclient.get(`${this.url}/associacoes/export/pdf`, {
      headers: this.httpHeaders,
      responseType: "blob",
      observe: "response",
      params
    }).pipe(catchError(handleError));
  }

  exportAssociacoesGrupoCarteiraXlsx(idGrupoCarteira: number, idCarteira: number, page: number, size: number): Observable<HttpResponse<Blob>> {
    let params = new HttpParams();
    if (idGrupoCarteira) params = params.set('idGrupo', idGrupoCarteira.toString());
    if (idCarteira) params = params.set('idCarteira', idCarteira.toString());

    return this.httpclient.get(`${this.url}/associacoes/export/xlsx`, {
      headers: this.httpHeaders,
      responseType: "blob",
      observe: "response",
      params
    }).pipe(catchError(handleError));
  }

  excluirAssociacaoGrupoCarteira(associacaoGrupoCarteiraVO: AssociacaoGrupoCarteiraVO): Observable<Object> {
    let params = new HttpParams();
    return this.httpclient.delete(`${this.url}/${associacaoGrupoCarteiraVO.idGrupoCarteira}/associacoes/${associacaoGrupoCarteiraVO.idCarteira}`, { headers: this.httpHeaders, params });
  }
}
